/**
 * GenericPurchaseModal — Create a non-Amazon purchase linked to an ML order.
 * Adapted from amazon/LinkPurchaseModal (GenericPurchaseModal) for the Order Hub.
 */
import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import {
  X, Loader2, Check, AlertCircle, Plus,
  Package, DollarSign, Truck, FileText,
} from 'lucide-react';
import type { UnifiedOrder } from './types';

interface GenericPurchaseModalProps {
  order: UnifiedOrder;
  sellerId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function GenericPurchaseModal({ order, sellerId, onClose, onSuccess }: GenericPurchaseModalProps) {
  const items = order.items || [];
  const [selectedItemIdx, setSelectedItemIdx] = useState(0);
  const selectedItem = items[selectedItemIdx] || items[0] || null;
  const defaultAsin = selectedItem?.seller_sku?.match(/^B0[A-Z0-9]{8,}$/)?.[0]
    || selectedItem?.seller_sku || '';
  const defaultQty = selectedItem?.quantity || 1;

  const [form, setForm] = useState({
    asin: defaultAsin,
    quantity: defaultQty,
    unit_cost: '',
    shipping_cost: '',
    tax_amount: '',
    supplier_name: '',
    invoice_number: '',
    payment_method: '',
    notes: '',
  });

  // Trackings list — at least one row (can be empty/unused)
  const [trackings, setTrackings] = useState<Array<{ tracking_number: string; carrier: string }>>([
    { tracking_number: '', carrier: '' },
  ]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Re-sync ASIN/qty when user changes the selected pack item
  useEffect(() => {
    if (!selectedItem) return;
    const asin = selectedItem.seller_sku?.match(/^B0[A-Z0-9]{8,}$/)?.[0]
      || selectedItem.seller_sku || '';
    setForm(prev => ({ ...prev, asin, quantity: selectedItem.quantity || 1 }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemIdx]);

  const updateField = (field: string, value: string | number) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const targetMlOrderId = selectedItem?.ml_order_id || order.ml_order_id || null;
  const headerLabel = targetMlOrderId ?? order.pack_id;

  const handleSubmit = async () => {
    if (!form.asin.trim() || !form.unit_cost) {
      setError('ASIN y costo unitario son obligatorios');
      return;
    }
    setSaving(true);
    setError('');

    const cleanTrackings = trackings
      .filter(t => t.tracking_number.trim().length > 0)
      .map(t => ({
        tracking_number: t.tracking_number.trim(),
        carrier: t.carrier.trim() || null,
      }));

    const res = await api.post<{ success: boolean; error?: string }>('/amazon/purchases/generic', {
      seller_id: sellerId,
      ml_order_id: targetMlOrderId,
      asin: form.asin,
      quantity: form.quantity,
      unit_cost: parseFloat(form.unit_cost),
      shipping_cost: parseFloat(form.shipping_cost || '0'),
      tax_amount: parseFloat(form.tax_amount || '0'),
      supplier_name: form.supplier_name || null,
      trackings: cleanTrackings,
      invoice_number: form.invoice_number || null,
      payment_method: form.payment_method || null,
      notes: form.notes || null,
    });

    if (res.success) {
      onSuccess();
    } else {
      setError(res.error || 'Error creando compra');
    }
    setSaving(false);
  };

  const totalCost = form.unit_cost
    ? ((parseFloat(form.unit_cost || '0') + parseFloat(form.shipping_cost || '0') + parseFloat(form.tax_amount || '0')) * form.quantity)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Plus size={18} className="text-[var(--color-primary)]" />
            <h3 className="font-bold text-[var(--color-text)]">
              Compra Manual {'\u2014'} Orden #{headerLabel}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        {/* Order summary */}
        <div className="px-4 pt-3 pb-2 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3 text-xs text-[var(--color-text-secondary)]">
            <span>{order.context_site}</span>
            <span>{order.currency_id} ${order.paid_amount.toFixed(2)}</span>
            <span>{order.items.length} item{order.items.length > 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          {/* Product */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1">
              <Package size={12} /> Producto
            </legend>
            {items.length > 1 && (
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">
                  Libro del pack
                </label>
                <select
                  value={selectedItemIdx}
                  onChange={(e) => setSelectedItemIdx(parseInt(e.target.value, 10))}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                >
                  {items.map((it, idx) => (
                    <option key={idx} value={idx}>
                      {(it.title || 'Sin título').slice(0, 60)} ({it.quantity}× ) — {it.seller_sku}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-[var(--color-text-tertiary)]">ASIN *</label>
                <input type="text" value={form.asin} onChange={(e) => updateField('asin', e.target.value.toUpperCase())}
                  placeholder="B0XXXXXXXX" maxLength={12}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Cantidad</label>
                <input type="number" value={form.quantity} onChange={(e) => updateField('quantity', parseInt(e.target.value) || 1)} min={1}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
            </div>
          </fieldset>

          {/* Costs */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1">
              <DollarSign size={12} /> Costos
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Costo Unit. * (USD)</label>
                <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => updateField('unit_cost', e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Envio (USD)</label>
                <input type="number" step="0.01" value={form.shipping_cost} onChange={(e) => updateField('shipping_cost', e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Tax (USD)</label>
                <input type="number" step="0.01" value={form.tax_amount} onChange={(e) => updateField('tax_amount', e.target.value)}
                  placeholder="0.00"
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
            </div>
            {totalCost > 0 && (
              <div className="text-right text-sm font-medium text-[var(--color-text)]">
                Total: ${totalCost.toFixed(2)}
              </div>
            )}
          </fieldset>

          {/* Supplier & Tracking */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1">
              <Truck size={12} /> Proveedor y Envio
            </legend>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)]">Proveedor</label>
              <input type="text" value={form.supplier_name} onChange={(e) => updateField('supplier_name', e.target.value)}
                placeholder="eBay, Walmart, etc."
                className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-tertiary)]">Trackings (opcional)</label>
              <div className="mt-0.5 space-y-1.5">
                {trackings.map((t, idx) => (
                  <div key={idx} className="flex gap-1.5 items-center">
                    <input
                      type="text"
                      value={t.tracking_number}
                      onChange={(e) => {
                        const next = [...trackings];
                        next[idx] = { ...next[idx], tracking_number: e.target.value };
                        setTrackings(next);
                      }}
                      placeholder={idx === 0 ? '1Z999... (tracking principal)' : 'Tracking del paquete extra'}
                      className="flex-1 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]"
                    />
                    <input
                      type="text"
                      value={t.carrier}
                      onChange={(e) => {
                        const next = [...trackings];
                        next[idx] = { ...next[idx], carrier: e.target.value };
                        setTrackings(next);
                      }}
                      placeholder="UPS, FedEx..."
                      className="w-32 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                    />
                    {trackings.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setTrackings(trackings.filter((_, i) => i !== idx))}
                        className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Quitar este tracking"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setTrackings([...trackings, { tracking_number: '', carrier: '' }])}
                  className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1 mt-1"
                >
                  <Plus size={12} /> Agregar otro tracking
                </button>
              </div>
            </div>
          </fieldset>

          {/* Invoice */}
          <fieldset className="border border-[var(--color-border)] rounded-lg p-3 space-y-3">
            <legend className="px-2 text-xs font-medium text-[var(--color-text-secondary)] uppercase flex items-center gap-1">
              <FileText size={12} /> Facturacion
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Nro. Factura</label>
                <input type="text" value={form.invoice_number} onChange={(e) => updateField('invoice_number', e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-tertiary)]">Metodo de Pago</label>
                <select value={form.payment_method} onChange={(e) => updateField('payment_method', e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]">
                  <option value="">{'\u2014'}</option>
                  <option value="credit_card">Tarjeta de Credito</option>
                  <option value="debit_card">Tarjeta de Debito</option>
                  <option value="paypal">PayPal</option>
                  <option value="wire_transfer">Transferencia</option>
                  <option value="cash">Efectivo</option>
                  <option value="other">Otro</option>
                </select>
              </div>
            </div>
          </fieldset>

          {/* Notes */}
          <div>
            <label className="text-xs text-[var(--color-text-tertiary)]">Notas</label>
            <textarea value={form.notes} onChange={(e) => updateField('notes', e.target.value)} rows={2}
              placeholder="Notas adicionales..."
              className="w-full mt-0.5 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]" />
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={saving || !form.asin.trim() || !form.unit_cost}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Crear Compra
          </button>
        </div>
      </div>
    </div>
  );
}
